import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertCircle, ChevronRight, Download, Search } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { localizedName } from '@/components/localized-name';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EVENT_TYPE_FILTERS } from '@/features/events/event-enums';
import { RecordPaymentDialog } from '@/features/events/record-payment-dialog';
import { BookingDetailSheet } from '@/features/manage-bookings/booking-detail-sheet';
import {
  useBookingHosts,
  useBookingLocations,
  useBookingServiceTypes,
  useExportBookings,
  type BookingSearchModel,
} from '@/features/manage-bookings/bookings.api';
import {
  BOOKING_STATUS,
  PAYMENT_CATEGORY,
  type AdminBookingRow,
} from '@/features/manage-bookings/types';
import { Meter, SectionTitle, StatGroup, StatTile } from '@/components/ui/stat-tile';
import { formatCurrency, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { toApiDate as apiDate } from '@/lib/store-time';
import { AddBookingDialog } from './add-booking-dialog';
import { BookingRowsSheet } from './booking-rows-sheet';
import { EventTimeslots } from './event-timeslots';
import { useBookingDashboard } from './sales.api';
import { fillPercent, type BookingEventRollup, type BookingTimeslot } from './types';

/**
 * Booking sales — what happened in aggregate, and at which events.
 *
 * The number this page exists to expose is "benefit value given": the report it
 * replaces counted purchases and sales only, so a booking paid with a
 * membership perk registered as zero everywhere and a class full of members
 * looked like a class that earned nothing.
 *
 * Unlike the memberships pages this one does expose a date range — booking
 * activity is inherently period-based, and there is no ambiguity about which
 * date it filters on: always the timeslot's start time.
 *
 * The tiles that used to switch to a bookings tab now navigate to the Bookings
 * page with the filter pre-applied, which is the same act with a URL.
 */

const ANY = 'ANY';

const startOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
};

const endOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0)
    .toISOString()
    .slice(0, 10);
};

/** Rates arrive as 0–100 here, unlike the 0–1 fill rates on the rollups. */
const pct = (value: number | null | undefined) => `${Math.round(value ?? 0)}%`;

/** One event's rollup, opening to its timeslots. */
function EventRow({
  row,
  model,
  onViewBooking,
  onAddBooking,
  onRecordPayment,
}: {
  row: BookingEventRollup;
  model: BookingSearchModel;
  onViewBooking: (id: string) => void;
  onAddBooking: (slot: BookingTimeslot, row: BookingEventRollup) => void;
  onRecordPayment: (booking: AdminBookingRow) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const fill = fillPercent(row.fillRate);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="hover:bg-accent/40 w-full text-left transition-colors">
        <div className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:gap-6">
          <div className="flex min-w-0 flex-1 items-start gap-2">
            <ChevronRight
              className={cn(
                'text-muted-foreground mt-0.5 size-4 shrink-0 transition-transform',
                open && 'rotate-90'
              )}
            />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{row.eventName}</p>
              <p className="text-muted-foreground truncate text-xs">
                {row.locationName}
              </p>
              <p className="text-muted-foreground text-xs">
                {t('sales.nTimeslots', { count: row.timeslots ?? 0 })}
              </p>
            </div>
          </div>

          <div className="sm:w-44">
            <Meter
              label={t('sales.nOfMSlots', {
                bookings: formatNumber(row.totalBookings),
                slots: formatNumber(row.slots),
              })}
              value={`${fill}%`}
              percent={fill}
            />
          </div>

          <div className="grid grid-cols-2 gap-3 sm:w-72 sm:grid-cols-4">
            <div>
              <p className="text-muted-foreground text-xs">
                {t('sales.bookings')}
              </p>
              <p className="text-sm tabular-nums">
                {formatNumber(row.totalBookings)}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">
                {t('sales.checkedIn')}
              </p>
              <p className="text-sm tabular-nums">
                {formatNumber(row.checkedIn)}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">
                {t('sales.usedABenefit')}
              </p>
              <p className="text-sm tabular-nums">
                {formatNumber(row.usedBenefit)}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">
                {t('sales.awaitingPayment')}
              </p>
              <p
                className={cn(
                  'text-sm tabular-nums',
                  (row.awaitingPayment ?? 0) > 0 && 'text-warning font-semibold'
                )}
              >
                {formatNumber(row.awaitingPayment)}
              </p>
            </div>
          </div>

          <div className="text-right sm:w-40">
            <p className="font-semibold tabular-nums">
              {formatCurrency(row.collected)}
            </p>
            <p className="text-muted-foreground text-xs tabular-nums">
              {formatCurrency(row.benefitValueRedeemed)}{' '}
              {t('sales.inBenefits')}
            </p>
          </div>
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent>
        {/* Timeslots are fetched when a row is opened, not with the rollup. */}
        <div className="bg-muted/40 border-t">
          {open && (
            <EventTimeslots
              eventId={row.eventId}
              model={model}
              onViewBooking={onViewBooking}
              onAddBooking={(slot) => onAddBooking(slot, row)}
              onRecordPayment={onRecordPayment}
            />
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function SalesPage() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const navigate = useNavigate();

  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [start, setStart] = useState(startOfMonth);
  const [end, setEnd] = useState(endOfMonth);
  const [locationId, setLocationId] = useState(ANY);
  const [eventType, setEventType] = useState(ANY);
  const [serviceTypeId, setServiceTypeId] = useState(ANY);
  const [hostId, setHostId] = useState(ANY);

  const [detailId, setDetailId] = useState<string | null>(null);
  const [paying, setPaying] = useState<AdminBookingRow | null>(null);
  const [adding, setAdding] = useState<{
    slot: BookingTimeslot;
    eventId: string | null;
    eventName: string | null;
    eventPrice: number | null;
  } | null>(null);

  const { data: locations } = useBookingLocations();
  const { data: serviceTypes } = useBookingServiceTypes();
  const { data: hosts } = useBookingHosts();

  const model: BookingSearchModel = useMemo(
    () => ({
      startDate: apiDate(start, false),
      endDate: apiDate(end, true),
      locationId: locationId === ANY ? null : locationId,
      eventId: null,
      serviceTypeId: serviceTypeId === ANY ? null : serviceTypeId,
      hostId: hostId === ANY ? null : hostId,
      eventType: eventType === ANY ? null : Number(eventType),
      paymentCategory: null,
      status: null,
      checkedIn: null,
      query,
      page: 0,
      size: 20,
    }),
    [start, end, locationId, serviceTypeId, hostId, eventType, query]
  );

  const dashboard = useBookingDashboard(model);
  const exportRows = useExportBookings();
  const loading = dashboard.isPending;
  // A refresh keeps the figures on screen and dims them; only a first
  // load, with nothing to dim, gets skeletons.
  const busy = dashboard.isFetching && !dashboard.isPending;

  const attendance = dashboard.data?.attendance;
  const howPaid = dashboard.data?.howItWasPaid;
  const money = dashboard.data?.money;
  const events = dashboard.data?.events ?? [];

  /**
   * Opening a tile is a question about that number, so it answers in place: a
   * drawer of the rows behind it, built from the same model the tile was, with
   * only the tile's own filter patched over it. The management grid is one
   * click away inside the drawer, for when the answer turns into work.
   */
  const [drill, setDrill] = useState<{
    title: string;
    patch: Partial<BookingSearchModel>;
  } | null>(null);

  const filterFields: FilterField[] = [
    { id: 'start', label: t('sales.from'), value: start, onChange: setStart, type: 'date', resetValue: '' },
    { id: 'end', label: t('sales.to'), value: end, onChange: setEnd, type: 'date', resetValue: '' },
    {
      id: 'location',
      label: t('sales.location'),
      value: locationId,
      onChange: setLocationId,
      options: [
        { value: ANY, label: t('sales.any') },
        ...(locations ?? []).map((l) => ({
          value: l.id,
          label: localizedName(l, language) || (l.name ?? ''),
        })),
      ],
    },
    {
      id: 'eventType',
      label: t('sales.eventType'),
      value: eventType,
      onChange: setEventType,
      sort: false,
      options: [
        { value: ANY, label: t('sales.any') },
        ...EVENT_TYPE_FILTERS.map((type) => ({
          value: String(type.value),
          label: t(type.labelKey),
        })),
      ],
    },
    {
      id: 'serviceType',
      label: t('sales.serviceType'),
      value: serviceTypeId,
      onChange: setServiceTypeId,
      options: [
        { value: ANY, label: t('sales.any') },
        ...(serviceTypes ?? []).map((s) => ({
          value: s.id,
          label: localizedName(s, language),
        })),
      ],
    },
    {
      id: 'host',
      label: t('sales.host'),
      value: hostId,
      onChange: setHostId,
      options: [
        { value: ANY, label: t('sales.any') },
        ...(hosts ?? []).map((h) => ({
          value: h.id,
          label: [h.firstName, h.lastName].filter(Boolean).join(' '),
        })),
      ],
    },
  ];

  function runExport() {
    exportRows.mutate(model, {
      onSuccess: (data) => {
        if (!data?.length) {
          toast.info(t('sales.nothingToExport'));
          return;
        }
        const keys = Object.keys(data[0]);
        const escape = (value: unknown) =>
          `"${String(value ?? '').replace(/"/g, '""')}"`;
        const csv = [
          keys.join(','),
          ...data.map((row) => keys.map((key) => escape(row[key])).join(',')),
        ].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `booking-sales-${start}-${end}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      },
      onError: () => toast.error(t('sales.exportFailed')),
    });
  }

  /** A totals row has nowhere to go in a card list, so it is pinned above it. */
  const totals = events.reduce(
    (sum, row) => ({
      totalBookings: sum.totalBookings + (row.totalBookings ?? 0),
      checkedIn: sum.checkedIn + (row.checkedIn ?? 0),
      usedBenefit: sum.usedBenefit + (row.usedBenefit ?? 0),
      awaitingPayment: sum.awaitingPayment + (row.awaitingPayment ?? 0),
      collected: sum.collected + (row.collected ?? 0),
      benefitValueRedeemed:
        sum.benefitValueRedeemed + (row.benefitValueRedeemed ?? 0),
    }),
    {
      totalBookings: 0,
      checkedIn: 0,
      usedBenefit: 0,
      awaitingPayment: 0,
      collected: 0,
      benefitValueRedeemed: 0,
    }
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('sales.title')}
        description={t('sales.subtitle')}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={runExport}
            disabled={exportRows.isPending}
          >
            <Download />
            <span className="hidden sm:inline">{t('sales.export')}</span>
          </Button>
        }
      />

      <FilterBar
        busy={dashboard.isFetching}
        search={
          <form
            role="search"
            onSubmit={(event) => {
              event.preventDefault();
              if (draft === query) void dashboard.refetch();
              setQuery(draft);
            }}
            className="flex gap-2"
          >
            <div className="relative flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t('sales.searchPlaceholder')}
                aria-label={t('sales.searchPlaceholder')}
                className="h-10 pl-9"
              />
            </div>
            <Button type="submit" className="h-10">
              <Search className="sm:hidden" />
              <span className="hidden sm:inline">{t('common.search')}</span>
            </Button>
          </form>
        }
        fields={filterFields}
      />

      {dashboard.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('sales.loadFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void dashboard.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      <Tabs defaultValue="overview" className="gap-5">
        <TabsList className="sm:w-auto">
          <TabsTrigger value="overview" className="sm:flex-none sm:px-5">
            {t('sales.overview')}
          </TabsTrigger>
          <TabsTrigger value="events" className="sm:flex-none sm:px-5">
            {t('sales.events')}
          </TabsTrigger>
        </TabsList>

        {/* ================================================ overview ==== */}
        <TabsContent value="overview" className="space-y-5">
          <StatGroup title={t('sales.attendanceGroup')}>
            <StatTile
              loading={loading}
              busy={busy}
              tone="primary"
              label={t('sales.bookings')}
              value={formatNumber(attendance?.totalBookings)}
            />
            <StatTile
              loading={loading}
              busy={busy}
              tone="good"
              label={t('sales.confirmed')}
              value={formatNumber(attendance?.confirmed)}
              onClick={() =>
                setDrill({
                  title: t('sales.confirmed'),
                  patch: { status: BOOKING_STATUS.confirmed },
                })
              }
            />
            <StatTile
              loading={loading}
              busy={busy}
              tone="warn"
              label={t('sales.pending')}
              value={formatNumber(attendance?.pending)}
              onClick={() =>
                setDrill({
                  title: t('sales.pending'),
                  patch: { status: BOOKING_STATUS.pending },
                })
              }
            />
            {/* Without this tile the statuses visibly fail to add up to the
                booking count, and the only number that explains the gap is the
                one nobody can see. */}
            <StatTile
              loading={loading}
              busy={busy}
              tone="bad"
              label={t('sales.cancelled')}
              value={formatNumber(attendance?.cancelled)}
              onClick={() =>
                setDrill({
                  title: t('sales.cancelled'),
                  patch: { status: BOOKING_STATUS.cancelled },
                })
              }
            />
            <StatTile
              loading={loading}
              busy={busy}
              tone="good"
              label={t('sales.checkedIn')}
              value={formatNumber(attendance?.checkedIn)}
              sub={`${pct(attendance?.attendanceRate)} ${t('sales.attendance')}`}
            />
            <StatTile
              loading={loading}
              busy={busy}
              tone="bad"
              label={t('sales.noShow')}
              value={formatNumber(attendance?.noShow)}
              tooltip={t('sales.noShowTooltip')}
            />
            <StatTile
              loading={loading}
              busy={busy}
              label={t('sales.fillRate')}
              value={pct(attendance?.fillRate)}
              sub={t('sales.nOfMSlots', {
                bookings: formatNumber(attendance?.totalBookings),
                slots: formatNumber(attendance?.slots),
              })}
            />
          </StatGroup>

          <StatGroup title={t('sales.howItWasPaidGroup')}>
            <StatTile
              loading={loading}
              busy={busy}
              tone="primary"
              label={t('sales.paidOnline')}
              value={formatNumber(howPaid?.paidOnline)}
              onClick={() =>
                setDrill({
                  title: t('sales.paidOnline'),
                  patch: { paymentCategory: PAYMENT_CATEGORY.paidOnline },
                })
              }
            />
            <StatTile
              loading={loading}
              busy={busy}
              tone="cash"
              label={t('sales.paidOffline')}
              value={formatNumber(howPaid?.paidOffline)}
              onClick={() =>
                setDrill({
                  title: t('sales.paidOffline'),
                  patch: { paymentCategory: PAYMENT_CATEGORY.paidOffline },
                })
              }
            />
            <StatTile
              loading={loading}
              busy={busy}
              tone="good"
              label={t('sales.usedABenefit')}
              value={formatNumber(howPaid?.usedBenefit)}
              sub={t('sales.membershipPerks')}
              onClick={() =>
                setDrill({
                  title: t('sales.usedABenefit'),
                  patch: { paymentCategory: PAYMENT_CATEGORY.usedBenefit },
                })
              }
            />
            <StatTile
              loading={loading}
              busy={busy}
              label={t('sales.freeEvent')}
              value={formatNumber(howPaid?.freeEvent)}
              onClick={() =>
                setDrill({
                  title: t('sales.freeEvent'),
                  patch: { paymentCategory: PAYMENT_CATEGORY.freeEvent },
                })
              }
            />
            <StatTile
              loading={loading}
              busy={busy}
              label={t('sales.comped')}
              value={formatNumber(howPaid?.comped)}
              tooltip={t('sales.compedTooltip')}
              onClick={() =>
                setDrill({
                  title: t('sales.comped'),
                  patch: { paymentCategory: PAYMENT_CATEGORY.comped },
                })
              }
            />
            <StatTile
              loading={loading}
              busy={busy}
              tone="warn"
              label={t('sales.awaitingPayment')}
              value={formatNumber(howPaid?.awaitingPayment)}
              sub={`${formatCurrency(money?.outstanding)} ${t('sales.outstanding').toLowerCase()}`}
              onClick={() =>
                setDrill({
                  title: t('sales.awaitingPayment'),
                  patch: { paymentCategory: PAYMENT_CATEGORY.awaitingPayment },
                })
              }
            />
            {/* The only tile here that means something is wrong. It is not a
                comp — nobody decided to give these away, the money simply
                cannot be accounted for. */}
            <StatTile
              loading={loading}
              busy={busy}
              tone="bad"
              label={t('sales.needsReview')}
              value={formatNumber(howPaid?.needsReview)}
              tooltip={t('sales.needsReviewTooltip')}
              onClick={() => navigate('/booking/manage/payment-review')}
            />
          </StatGroup>

          <StatGroup title={t('sales.money')}>
            <StatTile
              loading={loading}
              busy={busy}
              tone="good"
              label={t('sales.collected')}
              value={formatCurrency(money?.collected)}
              sub={`${t('sales.online')} ${formatCurrency(money?.collectedOnline)} · ${t('sales.offline')} ${formatCurrency(money?.collectedOffline)}`}
            />
            <StatTile
              loading={loading}
              busy={busy}
              tone="bad"
              label={t('sales.refunded')}
              value={formatCurrency(money?.refunded)}
            />
            <StatTile
              loading={loading}
              busy={busy}
              tone="good"
              label={t('sales.net')}
              value={formatCurrency(money?.net)}
            />
            <StatTile
              loading={loading}
              busy={busy}
              label={t('sales.averageTicket')}
              value={formatCurrency(money?.averageTicket)}
            />
            <StatTile
              loading={loading}
              busy={busy}
              tone="primary"
              label={t('sales.benefitValueGiven')}
              value={formatCurrency(money?.benefitValueRedeemed)}
              sub={t('sales.nBookingsAtList', {
                count: howPaid?.usedBenefit ?? 0,
              })}
              tooltip={t('sales.benefitValueGivenTooltip')}
            />
            <StatTile
              loading={loading}
              busy={busy}
              tone="warn"
              label={t('sales.outstanding')}
              value={formatCurrency(money?.outstanding)}
              onClick={() =>
                setDrill({
                  title: t('sales.outstanding'),
                  patch: { paymentCategory: PAYMENT_CATEGORY.awaitingPayment },
                })
              }
            />
          </StatGroup>
        </TabsContent>

        {/* ================================================== events ==== */}
        <TabsContent value="events" className="space-y-3">
          {loading && (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-24 rounded-xl" />
              ))}
            </div>
          )}

          {dashboard.isSuccess && events.length === 0 && (
            <div className="relative rounded-xl">
              <BusyOverlay show={busy} />
              <p className="text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm">
                {t('sales.noEvents')}
              </p>
            </div>
          )}

          {events.length > 0 && (
            <div className="relative">
              <BusyOverlay show={busy} />
              <div
                className={cn(
                  'space-y-3',
                  busy && 'opacity-60 transition-opacity'
                )}
              >
                  {/* Pinned above the list rather than a footer row: a totals row
                      that quietly vanishes on a phone is how a number gets
                      reported wrong. */}
                  <div className="bg-card border-l-primary space-y-2 rounded-xl border border-l-[3px] p-3">
                    <SectionTitle title={t('sales.total')} />
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                      <div>
                        <p className="text-muted-foreground text-xs">
                          {t('sales.bookings')}
                        </p>
                        <p className="font-semibold tabular-nums">
                          {formatNumber(totals.totalBookings)}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">
                          {t('sales.checkedIn')}
                        </p>
                        <p className="font-semibold tabular-nums">
                          {formatNumber(totals.checkedIn)}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">
                          {t('sales.usedABenefit')}
                        </p>
                        <p className="font-semibold tabular-nums">
                          {formatNumber(totals.usedBenefit)}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">
                          {t('sales.awaitingPayment')}
                        </p>
                        <p className="font-semibold tabular-nums">
                          {formatNumber(totals.awaitingPayment)}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">
                          {t('sales.collected')}
                        </p>
                        <p className="font-semibold tabular-nums">
                          {formatCurrency(totals.collected)}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">
                          {t('sales.benefitValueGiven')}
                        </p>
                        <p className="text-muted-foreground font-semibold tabular-nums">
                          {formatCurrency(totals.benefitValueRedeemed)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="divide-y overflow-hidden rounded-xl border">
                    {events.map((row) => (
                      <EventRow
                        key={row.eventId}
                        row={row}
                        model={model}
                        onViewBooking={setDetailId}
                        onAddBooking={(slot, event) =>
                          setAdding({
                            slot,
                            eventId: event.eventId,
                            eventName: event.eventName,
                            eventPrice: event.price,
                          })
                        }
                        onRecordPayment={setPaying}
                      />
                    ))}
                  </div>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <BookingRowsSheet
        model={model}
        patch={drill?.patch ?? null}
        title={drill?.title ?? ''}
        open={drill !== null}
        onOpenChange={(next) => {
          if (!next) setDrill(null);
        }}
        onViewBooking={setDetailId}
      />

      <BookingDetailSheet
        bookingId={detailId}
        open={detailId !== null}
        onOpenChange={(next) => {
          if (!next) setDetailId(null);
        }}
      />

      <AddBookingDialog
        timeslot={adding?.slot ?? null}
        eventId={adding?.eventId ?? null}
        eventName={adding?.eventName ?? null}
        eventPrice={adding?.eventPrice ?? null}
        open={adding !== null}
        onOpenChange={(next) => {
          if (!next) setAdding(null);
        }}
        onBooked={() => void dashboard.refetch()}
      />

      <RecordPaymentDialog
        targetId={paying?.id ?? null}
        purchases={[]}
        open={paying !== null}
        onOpenChange={(next) => {
          if (!next) setPaying(null);
        }}
        onSaved={() => void dashboard.refetch()}
      />
    </div>
  );
}
